import { TDateRange } from '../../api/types';
import React, { useEffect, useState } from 'react';
import { Tab, Tabs } from '@patternfly/react-core';
import MyPeriodButtons from './MyPeriodButtons';
import { useApplication } from '../user/ApplicationProvider';

type DatePeriodHtmlProps = {
	updateSelectedDates: (selectedDateRange?: TDateRange) => (TDateRange | undefined)[];
	resetDateClickCount: () => void;
	selectedRange: [TDateRange | undefined, TDateRange | undefined];
};

const DatePeriodHtml = (props: DatePeriodHtmlProps) => {
	const { updateSelectedDates, resetDateClickCount, selectedRange } = props;
	const { periods, datePeriods } = useApplication();

	const [activeTabKey, setActiveTabKey] = useState(selectedRange[0]?.period ?? 3);

	const getDateRangesByPeriodId = (periodId: number) => {
		return datePeriods.filter((dp) => dp.period === periodId);
	};

	const isPeriodVisible = (periodId: number) => {
		return periods.some((p) => p.id === periodId && p.visible === true);
	};

	const selectedRangeId = selectedRange[0]?.id;
	const desiredPeriod = selectedRange[0]?.period;

	// Re-sync active tab whenever the selected date range or periods change,
	// so preselection survives async loading and visibility changes.
	// Depends on the selection's primitive id/period rather than the
	// `selectedRange` array itself, since that array is a new reference on
	// every parent render (e.g. after a plain tab switch) even when the
	// underlying selection hasn't changed — using the array directly caused
	// this effect to immediately snap `activeTabKey` back after a manual tab click.
	useEffect(() => {
		if (desiredPeriod && isPeriodVisible(desiredPeriod)) {
			setActiveTabKey(desiredPeriod);
		} else if (!periods.some((p) => p.id === activeTabKey && p.visible === true)) {
			// current active tab isn't visible/valid anymore — fall back to first visible period
			const fallback = periods.find((p) => p.visible === true);
			if (fallback) {
				setActiveTabKey(fallback.id);
			}
		}
	}, [selectedRangeId, desiredPeriod, periods]);

	const handleTabSelect = (
		_event: React.MouseEvent<HTMLElement, MouseEvent>,
		tabIndex: string | number
	): void => {
		setActiveTabKey(Number(tabIndex));
		resetDateClickCount();
	};

	return (
		<div>
			<Tabs
				activeKey={activeTabKey}
				onSelect={handleTabSelect}
			>
				<Tab
					eventKey={0}
					title={'Period:'}
					isDisabled
				></Tab>

				{isPeriodVisible(5) && (
					<Tab
						eventKey={5}
						title={'Day'}
					>
						{activeTabKey === 5 && (
							<MyPeriodButtons
								datePeriods={getDateRangesByPeriodId(5)}
								selectedRange={selectedRange}
								updateSelectedDates={updateSelectedDates}
							/>
						)}
					</Tab>
				)}

				{isPeriodVisible(4) && (
					<Tab
						eventKey={4}
						title={'Week'}
					>
						{activeTabKey === 4 && (
							<MyPeriodButtons
								datePeriods={getDateRangesByPeriodId(4)}
								selectedRange={selectedRange}
								updateSelectedDates={updateSelectedDates}
							/>
						)}
					</Tab>
				)}

				{isPeriodVisible(3) && (
					<Tab
						eventKey={3}
						title={'Month'}
					>
						{activeTabKey === 3 && (
							<MyPeriodButtons
								datePeriods={getDateRangesByPeriodId(3)}
								selectedRange={selectedRange}
								updateSelectedDates={updateSelectedDates}
							/>
						)}
					</Tab>
				)}

				{isPeriodVisible(2) && (
					<Tab
						eventKey={2}
						title={'Quarter'}
					>
						{activeTabKey === 2 && (
							<MyPeriodButtons
								datePeriods={getDateRangesByPeriodId(2)}
								selectedRange={selectedRange}
								updateSelectedDates={updateSelectedDates}
							/>
						)}
					</Tab>
				)}

				{isPeriodVisible(1) && (
					<Tab
						eventKey={1}
						title={'Year'}
					>
						{activeTabKey === 1 && (
							<MyPeriodButtons
								datePeriods={getDateRangesByPeriodId(1)}
								selectedRange={selectedRange}
								updateSelectedDates={updateSelectedDates}
							/>
						)}
					</Tab>
				)}
			</Tabs>
			<br />
		</div>
	);
};

export default DatePeriodHtml;
